import { useEffect, useState } from "react";

import {
  TextField, Button, Typography, Select,
  AutocompleteMultipleSelect, Checkbox
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { getCountyList } from "../../api/Geography/GeographyApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import { getUserById, saveUser, getUserOibServiceData, getRoleList } from "../../api/User/UserApi";
import { useAppDispatch } from "../../app/store";
import { ContentArea, FormCard, ToolbarEdit, ToolbarTop } from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  ValidationFailure,
  ServiceResultStatus, GreatGrandParentCodeBookPagedFilter, Role as RoleEnum
} from "../../models";
import { InstitutionCodeBook, InstitutionCodeBookSearch } from "../../models/Institution";
import { User, ClaimType, Role } from "../../models/User";
import { userDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const oibRegex = /^\d{11}$/;

  const createDefaultItem = (): User => {
    return {
      id: 0,
      oib: "",
      firstName: "",
      lastName: "",
      userRoles: [],
      oibValidated: false,
    };
  };
  const [item, setItem] = useState<User>(createDefaultItem());
  const [roles, setRoles] = useState<Role[]>([]);
  const [errors, setErrors] = useState<ValidationFailure[]>()
  const [oibSearched, setOibSearched] = useState<boolean>(false);

  // ClaimType now comes from auth.currentRole.claimType
  //const [claimType, setClaimType] = useState<ClaimType|undefined>();
  // Locations now comes from auth.currentRole.locations
  //const [locations, setLocations] = useState<number[]|undefined>();
  // AssignableRoles now come from auth.currentRole.rolePermissions
  //const [assignableRoles, setAssignableRoles] = useState<RoleEnum[]|undefined>([]);

  // Counties are filtered on load when the current user is at 
  // the County level.
  const [counties, setCounties] = useState<CodeBook[]>();
  // Institutions are filtered on search when the current user is 
  // at the Institution level.
  const [filteredInstitutions, setFilteredInstitutions] = useState<InstitutionCodeBook[]>([]);

  useEffect(() => {
    (async () => {
      var roles = await getRoleList();
      setRoles(roles);
      if (auth.currentRole && id) {
        var result = await getUserById(+id);
        if (result) {
          setItem(result);

          //setAssignableRoles(result.rolePermissions);
          //setLocations(result.locations);
          //setClaimType(result.claimType);

          if (auth.currentRole.claimType !== ClaimType.Institution) {
            var counties = await getCountyList();
            if (auth.currentRole.claimType === ClaimType.County)
              counties = counties.filter(val => auth.currentRole!.locations?.find(l => l === val.id) !== undefined);
            setCounties(counties);
          }
        }
      }
    })();
    dispatch(resetIsEdited())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth.currentRole]);

  const handleOibChanged = (e: any) => {
    const oib = e.target.value as string;
    const newItem: User = {
      ...item,
      oib: oib,
      oibValidated: false,
      firstName: undefined,
      lastName: undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited())
  };
  const handleEmailChanged = (e: any) => {
    const lastName = e.target.value as string;
    handleEmailChangedString(lastName);
  };
  const handleEmailChangedString = (t: string | undefined) => {
    const newItem: User = {
      ...item,
      email: t && t.length > 0 ? t : undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited())
  };
  const handleTelephoneChanged = (e: any) => {
    const firstName = e.target.value as string;
    handleTelephoneChangedString(firstName);
  };
  const handleTelephoneChangedString = (t: string | undefined) => {
    const newItem: User = {
      ...item,
      telephone: t && t.length > 0 ? t : undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited())
  };
  const handleOibSystem = async () => {
    if (item.oib && oibRegex.test(item.oib)) {
      var result = await getUserOibServiceData(item.oib);
      setOibSearched(true);
      if (result && result.status === ServiceResultStatus.Success
        && result.item) {
        const newItem: User = {
          ...item,
          id: result.item.id ?? 0,
          firstName: result.item.firstName,
          lastName: result.item.lastName,
          oibValidated: result !== null,
        };
        setItem(newItem);
        dispatch(setIsEdited())
      }
    }
  };
  const handleInstitutionFilterChange = async (filter: string) => {
    // If the current role is at the Institution level, filter by 
    // available locations. The county level should never call this 
    // function. The global level has no restrictions.
    const search: InstitutionCodeBookSearch = {
      search: filter,
      onlyActive: false,
      includeOutsideEnrollmentArea: true,
      countyIds: auth.currentRole?.claimType === ClaimType.County ? auth.currentRole.locations : undefined,
      ids: auth.currentRole!.claimType === ClaimType.Institution ? auth.currentRole!.locations : undefined,
    };
    var result = await searchInstitutionNew(search);
    setFilteredInstitutions(result);
  };
  const handleRoleChangeBoolean = (roleId: number, enabled: boolean) => {
    // Only called for Global roles.
    // If the current user is at the Institution level or the 
    // County level, this function should never be called.
    // The global level has no restrictions.
    if (enabled) {
      const role = roles.find(val => val.id === roleId);
      if (role) {
        var userRoles = item.userRoles;
        userRoles.push({ role: { id: role.id, name: role.name } })
        const newItem: User = {
          ...item,
          userRoles: userRoles
        };
        setItem(newItem);
        dispatch(setIsEdited())
      }
    } else {
      const userRoles = item.userRoles.filter(val => val.role.id !== roleId);
      const newItem: User = {
        ...item,
        userRoles: userRoles
      };
      setItem(newItem);
      dispatch(setIsEdited())
    }
  }
  const handleRoleChangeNumber = (roleId: number, locations: number[] | undefined) => {
    // Only called from the County multi-select.
    // If the current user is at the County level, the available 
    // counties are already filtered. The Institution level should never call 
    // this function. The global level has no restrictions.
    if (locations) {
      var userRoles = item.userRoles;
      var userRole = userRoles.find(val => val.role.id === roleId);
      if (!userRole) {
        const role = roles.find(val => val.id === roleId);
        if (role) {
          userRole = { role: { id: role.id, name: role.name } };
          userRoles.push(userRole);
        }
      }
      if (userRole) {
        userRole.locations = locations.map<CodeBook>(val => { return { id: val, name: "" }; });
        const newItem: User = {
          ...item,
          userRoles: userRoles
        };
        setItem(newItem);
        dispatch(setIsEdited())
      }
    } else {
      const userRoles = item.userRoles.filter(val => val.role.id !== roleId);
      const newItem: User = {
        ...item,
        userRoles: userRoles
      };
      setItem(newItem);
      dispatch(setIsEdited())
    }
  }
  const handleRoleChangeCodeBook = (roleId: number, locations: CodeBook[] | undefined) => {
    // Only called from the Institution multi-select.
    // If the current user is at the Institution level, the available 
    // institutions are already filtered. The county level should never call 
    // this function. The global level has no restrictions.
    if (locations) {
      var userRoles = item.userRoles;
      var userRole = userRoles.find(val => val.role.id === roleId);
      if (!userRole) {
        const role = roles.find(val => val.id === roleId);
        if (role) {
          userRole = { role: { id: role.id, name: role.name } };
          userRoles.push(userRole);
        }
      }
      if (userRole) {
        userRole.locations = locations;
        const newItem: User = {
          ...item,
          userRoles: userRoles
        };
        setItem(newItem);
        dispatch(setIsEdited())
      }
    } else {
      const userRoles = item.userRoles.filter(val => val.role.id !== roleId);
      const newItem: User = {
        ...item,
        userRoles: userRoles
      };
      setItem(newItem);
      dispatch(setIsEdited())
    }
  }

  /**
   * Renders the possible UserRoles which are assignable by the current user.
   * @returns 
   */
  const renderUserRoles = () => {
    return (
      <Row>
        {roles.filter(val => auth.currentRole?.rolePermissions?.find(r => r === val.id) !== undefined).map(val =>
        (<>
          <Col md={4}>
            {renderRole(val, item)}
          </Col>
          <Col md={8}>
            {renderLocations(val, item)}
          </Col>
        </>)
        )}
      </Row>
    )
  };
  /**
   * Renders the role which is assignable by the current user for the specified 
   * user.
   * @param role The role which is assignable by the current user.
   * @param item The edited user to which the role may apply.
   * @returns 
   */
  const renderRole = (role: Role, item: User) => {
    if (role.claimTypeId === ClaimType.Global) {
      return (
        <Checkbox
          checked={item.userRoles.find(ur => ur.role.id === role.id) !== undefined}
          label={role.name}
          disabled={!role.isEditable}
          onChange={(e) => handleRoleChangeBoolean(role.id, e.target.checked)}
        />
      )
    } else {
      return (
        <Typography color="light">
          {role.name}
        </Typography>
      )
    }
  };
  /**
   * Renders the locations which are assignable by the current user for the specified 
   * user.
   * @param role The role which is assignable by the current user.
   * @param item The edited user to which the role may apply.
   * @returns 
   */
  const renderLocations = (role: Role, item: User) => {
    if (role.id === RoleEnum.CountyCommittee
        || role.id === RoleEnum.InstitutionCommittee) {
        return (
          <TextField
            disabled
            label={role.claimTypeId === ClaimType.County ? t("common.schoolCandidateModel.countyList") : t("common.schoolCandidateModel.institutionList")}
            readonly
            value={item.userRoles.find(r => r.role.id === role.id)?.locations?.map(l => l.name).join(", ")}
          />
        )
    }
    if (role.claimTypeId !== ClaimType.Global) {
      if (role.claimTypeId === ClaimType.County) {
        return (
          <Select
            multiple
            label={t("common.schoolCandidateModel.countyList")}
            name={role.name}
            value={item.userRoles.find(r => r.role.id === role.id)?.locations?.map(l => l.id)}
            onChange={(e) => handleRoleChangeNumber(role.id, e.target.value)}
            selectData={codeBookToSelectData(counties)}
            disabled={!role.isEditable}
          />
        )
      } else if (role.claimTypeId === ClaimType.Institution) {
        return (
          <AutocompleteMultipleSelect<CodeBook>
            label={t("common.schoolCandidateModel.institutionList")}
            name={role.name}
            value={item.userRoles.find(r => r.role.id === role.id)?.locations ?? []}
            valueProp="id"
            textProp="name"
            onChange={(e) => handleRoleChangeCodeBook(role.id, e.target.value)}
            selectOptions={filteredInstitutions}
            onFilterChange={handleInstitutionFilterChange}
            maximumSelectedItems={50}
          />
        )
      }
    }
  }

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.user.pageNameDetails")}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <TextField
              label={t("common.user.oib")}
              name={"item.oib"}
              value={item.oib}
              onChange={handleOibChanged}
              errorText={errors?.find(e => e.propertyName === "OIB")?.message}
              plaintext={id !== "0"}
            />
          </Col>
          <Col md={5}>
            <Button
              onClick={handleOibSystem}
              disabled={!item.oib || !oibRegex.test(item.oib)}
            >
              {t("common.user.connectOibSystem")}
            </Button>
          </Col>
          <Col md={5}>
            <Alert
              dismissible={false}
              show={oibSearched || id != "0"}
              variant={item.oibValidated ? "success" : "danger"}
            >
              {item.oibValidated ? t("common.user.oibSuccess") : t("common.user.oibFailure")}
            </Alert>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.user.lastName")}
              name={"item.lastName"}
              value={item.lastName}
              readonly
              plaintext
            // onChange={handleLastNameChanged}
            // errorText={errors?.find(e => e.propertyName === "LastName")?.message}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.user.firstName")}
              name={"item.firstName"}
              value={item.firstName}
              readonly
              plaintext
            // onChange={handleFirstNameChanged}
            // errorText={errors?.find(e => e.propertyName === "FirstName")?.message}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.email")}
              name={"item.email"}
              value={item.email}
              onChange={handleEmailChanged}
              required
              min={6}
              max={250}
              errorText={errors?.find(e => e.propertyName === "Email")?.message}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.phone")}
              name={"item.telephone"}
              value={item.telephone}
              onChange={handleTelephoneChanged}
              min={6}
              max={250}
              errorText={errors?.find(e => e.propertyName === "Telephone")?.message}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography color="light">
              {t("common.user.roles")}
            </Typography>
          </Col>
        </Row>
        {renderUserRoles()}
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={item.oibValidated ? "show" : "disable"}
        redirectUrlSaveOK={userDetailUrl}
        handleSave={async () => saveUser(item)}
        setSaveErrors={setErrors}
      />
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
    </ContentArea>
  );
}


